import React, { useEffect, useState } from 'react';

interface AdUnitProps {
  slot: string;
  format?: 'auto' | 'fluid' | 'rectangle';
  style?: React.CSSProperties;
  className?: string;
  position: 'top' | 'middle' | 'bottom';
}

const AdUnit: React.FC<AdUnitProps> = ({ slot, format = 'auto', style, className, position }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    try {
      const adsenseId = (window as any).adsenseId;
      
      // Only attempt to load ads if we have an AdSense ID
      if (adsenseId) {
        // Push the command to Google AdSense
        ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
        setIsLoaded(true);
      } else {
        setError('AdSense not configured');
      }
    } catch (e) {
      console.error('AdSense error:', e);
      setError(e instanceof Error ? e.message : 'Failed to load advertisement');
    }
  }, []);

  // If AdSense isn't configured, return null to prevent empty space
  if (!isLoaded && error === 'AdSense not configured') {
    return null;
  }

  // If there's an error loading the ad, show a placeholder in development
  if (error && process.env.NODE_ENV === 'development') {
    return (
      <div className={`w-full flex justify-center my-4 ${className || ''}`}>
        <div 
          style={style || { width: '100%', minHeight: '90px' }}
          className="bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-gray-400 text-sm"
        >
          Advertisement Placeholder
          {error !== 'AdSense not configured' && (
            <span className="text-red-400 ml-2">({error})</span>
          )}
        </div>
      </div>
    );
  }

  // Return the actual ad unit with proper labeling and styling
  return (
    <div className={`w-full flex flex-col items-center my-8 ${className || ''}`}>
      <div className="w-full max-w-4xl">
        <div className="text-xs text-gray-400 mb-2 text-center">Advertisement</div>
        <div className={`w-full bg-white/5 rounded-lg p-4 border border-white/10 ${
          position === 'middle' ? 'my-8' : position === 'bottom' ? 'mt-8' : 'mb-8'
        }`}>
          <ins
            className="adsbygoogle"
            style={style || { display: 'block', width: '100%', minHeight: '90px' }}
            data-ad-client={(window as any).adsenseId}
            data-ad-slot={slot}
            data-ad-format={format}
            data-full-width-responsive="true"
          />
        </div>
      </div>
    </div>
  );
};

export default AdUnit;